/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ILAC_AuthToken, ILAC_StoreOrderState } from '../custom-actions/DataServiceEntities.g';
import { lAC_ITSGetOrderStateAsync } from '../custom-actions/DataActionExtension.g';
import { withFreshToken } from '../utils/authService';

/**
 * What the store has done with an order, asked of the order itself. The suspended cart lookup only says what
 * the store still holds, and an order that is no longer there may have been completed, voided, never
 * suspended, or simply not reached.
 */
export class GetOrderStateInput implements Msdyn365.IActionInput {
    public storeOrderId: string;
    public channelId: number;
    public authTokenCookie: ILAC_AuthToken;

    constructor(storeOrderId: string, channelId: number, authTokenCookie: ILAC_AuthToken) {
        this.storeOrderId = storeOrderId;
        this.channelId = channelId;
        this.authTokenCookie = authTokenCookie;
    }

    public getCacheKey = () => ['LACGETORDERSTATE', this.channelId, this.storeOrderId].join('_');
    public getCacheObjectType = () => 'LACGETORDERSTATE';

    // The answer changes the moment the till finishes with the order, and this channel acts on it by taking
    // the order away from the guest, so it is never served from a cache.
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

// A failure has to reject. An unknown state means the store has no such order, which this channel acts on,
// so a call that never reached the store must not be reported as one that did.
async function getOrderStateAsync(input: GetOrderStateInput, ctx: Msdyn365.IActionContext): Promise<ILAC_StoreOrderState | undefined> {
    if (!input.storeOrderId || !input.authTokenCookie?.Token) {
        return undefined;
    }

    return withFreshToken(ctx, input.authTokenCookie, async carried =>
        lAC_ITSGetOrderStateAsync({ callerContext: ctx }, carried, input.storeOrderId, input.channelId)
    );
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ILAC_StoreOrderState | undefined>>getOrderStateAsync,
    id: 'GetOrderState'
});
